import { UseQueryOptions } from 'react-query';
import { AxiosResponse } from 'axios';
import { ServiceError } from '../../../../../hooks/error';
import { useCustomMutate, useCustomPostQuery, useCustomQuery } from '../../../../../hooks/query';
import { KeyValueResponse } from '../../../../model';
import {
  getCoordinatorApplicationDownloadSummary,
  getCoordinatorResitAccess,
  getCSVTemplateDownloadUrl,
  getPreSignUploadUrl,
  getResitApplicationStatus,
  getResitConfiguration,
  getResitRegistrationOptions,
  getSimplifiedPaymentDetail,
  getUploadFileScanStatus,
  getWindowPeriodByExamLevel,
  postCandidateGroupFeeSaving,
  postCoordinatorDownloadSummary,
  postGroupFee,
  postResitCandidateSearch,
  postSetUploadStatusUploaded,
  postSubjectSummary,
  postValidateUploadFile,
} from '../api-services/api';
import {
  GET_COORDINATOR_RESIT_REGISTER_OPTIONS,
  GET_FILE_SCAN_STATUS,
  GET_PRE_SIGN_UPLOAD_URL,
  GET_RESIT_APPLICATION_DOWNLOAD_SUMMARY,
  GET_RESIT_APPLICATION_STATUS,
  GET_RESIT_CONFIGURATION,
  GET_RESIT_COORDINATOR_ACCESS_SUMMARY,
  GET_RESIT_SIMPLIFIED_PAYMENT_DETAIL,
  GET_TEMPLATE_DOWNLOAD_URL,
  GET_WINDOW_PERIOD_BY_EXAM_LEVEL,
  POST_CANDIDATE_SEARCH,
  POST_RESIT_CANDIDATE_GROUP_FEE_SAVING,
  POST_RESIT_COORDINATOR_DOWNLOAD_SUMMARY,
  POST_RESIT_GROUP_FEE,
  POST_RESIT_SUBJECT_FEE_SUMMARY,
  POST_UPLOAD_FILE_STATUS,
  POST_VALIDATE_UPLOAD_FILE,
} from './keys';
import {
  ResitCandidateSearchRequest,
  WindowPeriodByExamLevelResponse,
} from '../api-services/models';
import {
  CandidateResitRegistrationSchema,
  GroupResitSchema,
  ResitCoordinatorAccess,
  ResitSimplePaymentDetail,
} from '../../../../../models/@candidate/candidateResitRegistrationSchema';
import {
  PreSignUploadURLResponse,
  ResitConfigurationResponse,
  ResitDownloadResponse,
  ResitGroupApplicationStatus,
  ResitRegisterFileScanningStatusResponse,
  ResitRegisterTemplatePreSignUrlResponse,
  ResitSummaryDownloadResponse,
} from '../model';

/** to get csv template for resit register
 * @return => string ("download url to download the csv")
 * */
export const useGetCSVDownloadUrl = (
  queryOptions?:
    | UseQueryOptions<
        ResitRegisterTemplatePreSignUrlResponse,
        ServiceError,
        ResitRegisterTemplatePreSignUrlResponse
      >
    | undefined
) =>
  useCustomQuery<ResitRegisterTemplatePreSignUrlResponse>(
    GET_TEMPLATE_DOWNLOAD_URL,
    () => getCSVTemplateDownloadUrl().then((response) => response.data),
    {
      ...queryOptions,
      cacheTime: 0,
      staleTime: 0,
      refetchOnMount: false,
      retryOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      refetchOnWindowFocus: false,
      enabled: false,
    },
    false,
    false
  );

/** to get coordinator resit register options
 * @returns KeyValueResponse[]
 * */
export const useGetCoordinatorResitRegisterOptions = (
  queryOptions?: UseQueryOptions<KeyValueResponse[], ServiceError> | undefined
) =>
  useCustomQuery<KeyValueResponse[]>(
    GET_COORDINATOR_RESIT_REGISTER_OPTIONS,
    () => getResitRegistrationOptions().then((res) => res.data),
    queryOptions,
    false,
    false
  );

/** to get window period by exam level
 * */
export const useGetWindowPeriodByExamLevel = (
  examLevel: string,
  queryOptions?:
    | UseQueryOptions<
        WindowPeriodByExamLevelResponse,
        ServiceError,
        WindowPeriodByExamLevelResponse
      >
    | undefined
) =>
  useCustomQuery<WindowPeriodByExamLevelResponse>(
    GET_WINDOW_PERIOD_BY_EXAM_LEVEL,
    () => getWindowPeriodByExamLevel(examLevel).then((response) => response.data),
    queryOptions,
    false,
    false
  );

/**
 * coordinator post candidate search to add to resit candidate list
 */
export const useResitCandidateSearch = (
  onSuccess: (res: AxiosResponse<CandidateResitRegistrationSchema>) => void,
  onError: (res: ServiceError) => void
) =>
  useCustomMutate<AxiosResponse<CandidateResitRegistrationSchema>, ResitCandidateSearchRequest>(
    POST_CANDIDATE_SEARCH,
    postResitCandidateSearch,
    {
      onSuccess: (res) => onSuccess(res),
      onError: (err) => onError(err),
    },
    true,
    false
  );

/** to get pre sign URL for upload
 * @return => string
 * */
export const useGetPreSignUploadUrl = (
  queryOptions?:
    | UseQueryOptions<PreSignUploadURLResponse, ServiceError, PreSignUploadURLResponse>
    | undefined
) =>
  useCustomQuery<PreSignUploadURLResponse>(
    GET_PRE_SIGN_UPLOAD_URL,
    () => getPreSignUploadUrl().then((response) => response.data),
    {
      ...queryOptions,
      cacheTime: 0,
      staleTime: 0,
      refetchOnMount: false,
      retryOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      refetchOnWindowFocus: false,
      enabled: false,
    },
    false,
    false
  );

/** to validate Upload File
 * @return => Axios response
 * */
export const usePostValidateUploadFile = (
  onSuccess: (res: AxiosResponse<CandidateResitRegistrationSchema[]>) => void,
  onError: (res: ServiceError) => void
) =>
  useCustomMutate(
    POST_VALIDATE_UPLOAD_FILE,
    postValidateUploadFile,
    {
      onSuccess,
      onError,
    },
    false,
    false
  );

/** to get upload file status from s3
 * @return => Axios response
 * */
export const usePostFileStatus = (onError: (res: any) => void) =>
  useCustomMutate(
    POST_UPLOAD_FILE_STATUS,
    postSetUploadStatusUploaded,
    {
      onError,
    },
    false,
    false
  );

/** to get file status
 * */
export const useGetFileScanningStatus = (
  key: string,
  queryOptions?:
    | UseQueryOptions<
        ResitRegisterFileScanningStatusResponse,
        ServiceError,
        ResitRegisterFileScanningStatusResponse
      >
    | undefined
) =>
  useCustomQuery(
    GET_FILE_SCAN_STATUS,
    () => getUploadFileScanStatus(key).then((res) => res.data),
    queryOptions,
    false,
    false
  );

/** to get subject summary by subjects
 * @return => GetSubjectSummaryResponse
 * */
export const usePostSubjectSummary = (onSuccess: (res: any) => void, onError: (res: any) => void) =>
  useCustomMutate(
    POST_RESIT_SUBJECT_FEE_SUMMARY,
    postSubjectSummary,
    {
      onSuccess,
      onError,
    },
    false,
    false
  );

/**
 * To get group total fee and gst with all candidates' selected subjects
 * @param payload
 * @param onSuccess
 * @param onError
 */
export const usePostGroupFee = (
  payload: GroupResitSchema,
  onSuccess: (res: AxiosResponse<GroupResitSchema>) => void,
  onError: (res: any) => void
) =>
  useCustomPostQuery(
    [POST_RESIT_GROUP_FEE, payload],
    postGroupFee,
    { onSuccess, onError },
    payload,
    true,
    false
  );

/**
 * To save group total fee and gst with all candidates' selected subjects so that e-mail can be sent to candidates
 * @param onSuccess
 * @param onError
 * @returns
 */
export const usePostCandidateGroupFeeSaving = (onSuccess: () => void, onError: () => void) =>
  useCustomMutate(POST_RESIT_CANDIDATE_GROUP_FEE_SAVING, postCandidateGroupFeeSaving, {
    onSuccess,
    onError,
  });

/**
 * To retrieve simple payment detail with internalTransactionRef
 * @param onSuccess function after query success
 * @param onError function after query fail
 * @param internalTransactionRef internal transaction ref to search
 * @returns
 */
export const useSimplePaymentDetail = (
  onSuccess: (res: ResitSimplePaymentDetail) => void,
  onError: (res: any) => void,
  internalTransactionRef: string
) =>
  useCustomQuery<ResitSimplePaymentDetail>(
    GET_RESIT_SIMPLIFIED_PAYMENT_DETAIL,
    () => getSimplifiedPaymentDetail(internalTransactionRef).then((res) => res.data),
    {
      onSuccess,
      onError,
    },
    true
  );

/**
 * To retrieve generating file uuid to start the file pooling download for resit summary status (with email)
 * @param onSuccess function after query success
 * @param onError function after query fail
 * @returns
 */
export const useCoordinatorDownloadSummary = (
  onSuccess: (res: AxiosResponse<ResitSummaryDownloadResponse>) => void,
  onError: () => void
) =>
  useCustomMutate([POST_RESIT_COORDINATOR_DOWNLOAD_SUMMARY], postCoordinatorDownloadSummary, {
    onSuccess,
    onError,
  });

/**
 * To retrieve coordinator access in resit, to determine whether can make payment on behalf of candidate or only can send email
 * @param onSuccess function after query success
 * @param onError function after query fail
 * @param examCode examCode the coordinator to check on
 * @returns
 */
export const useCoordinatorResitAccess = (
  onSuccess: (res: any) => void,
  onError: (res: any) => void,
  examCode: string | undefined
) =>
  useCustomQuery<ResitCoordinatorAccess>(
    GET_RESIT_COORDINATOR_ACCESS_SUMMARY,
    () => getCoordinatorResitAccess(examCode || '').then((res) => res.data),
    {
      onSuccess,
      onError,
      enabled: !!examCode,
    },
    true
  );

export const useResitApplicationStatus = (
  onSuccess: (res: ResitGroupApplicationStatus) => void,
  onError: (res: any) => void,
  id: string
) =>
  useCustomQuery<ResitGroupApplicationStatus>(
    [GET_RESIT_APPLICATION_STATUS, id],
    () => getResitApplicationStatus(id).then((res) => res.data),
    {
      onSuccess,
      onError,
    }
  );

/**
 * To retrieve generating file uuid to start the file pooling download for resit application status (with email)
 * @param onSuccess
 * @param onError
 * @returns
 */
export const useCoordinatorApplicationDownloadSummary = (
  onSuccess: (res: AxiosResponse<ResitDownloadResponse>) => void,
  onError: () => void
) =>
  useCustomMutate(
    [GET_RESIT_APPLICATION_DOWNLOAD_SUMMARY],
    getCoordinatorApplicationDownloadSummary,
    {
      onSuccess,
      onError,
    }
  );

/**
 * To retrieve generating file uuid to start the file pooling download for resit application status (with email)
 * @param onSuccess
 * @param onError
 * @returns
 */
export const useResitConfiguration = (
  onSuccess?: (res: AxiosResponse<ResitConfigurationResponse>) => void,
  onError?: () => void
) =>
  useCustomQuery([GET_RESIT_CONFIGURATION], getResitConfiguration, {
    onSuccess,
    onError,
  });
